import React, { useState } from 'react'
import {
  BlockStack,
  Box,
  Card,
  Divider,
  Layout,
  SkeletonBodyText,
  SkeletonDisplayText,
  Text,
  Button,
  ProgressBar,
  InlineGrid,
} from '@shopify/polaris'
import { useTranslation } from 'react-i18next'
import BillingModal from '../../../components/BillingModal'
import { PricingStrategies } from '../../../constants/enums'
import { getPlanType } from './accountHelpers'

// Import plan-specific account cards
import ServesAccountCard from './ServesAccountCard'
import OrderCountAccountCard from './OrderCountAccountCard'
import FlatAccountCard from './FlatAccountCard'
import RevenueAccountCard from './RevenueAccountCard'

function AccountSection({
  config,
  billingCycle,
  shopPlan,
  dashboardData,
  pricing,
  loading,
}) {
  const { t } = useTranslation()
  const [bmOpen, setBMOpen] = useState(false)
  
  // Check if all required data is loaded
  const isDataLoading = loading || 
    !shopPlan || 
    !billingCycle || 
    !dashboardData?.config || 
    !pricing?.plans || 
    pricing?.isLoading
  
  // Handle loading state - show skeleton until all data is ready
  if (isDataLoading) {
    return <Skeleton />
  }
  
  // Get the pricing strategy - use shopConfig's strategy as fallback
  const configStrategy = dashboardData?.config?.pricingStrategy || PricingStrategies.SERVES
  const pricingStrategy = shopPlan?.strategy || configStrategy

  // Callback for opening billing modal
  const handleOpenBilling = () => {
    setBMOpen(true)
  }

  // Render the appropriate account card based on pricing strategy
  const renderAccountCard = () => {
    const commonProps = {
      config,
      billingCycle,
      shopPlan,
      dashboardData,
      pricing,
      onOpenBilling: handleOpenBilling,
      changePlanCB: pricing.changePlan,
    }

    switch (pricingStrategy) {
      case PricingStrategies.ORDER_COUNT:
        return <OrderCountAccountCard {...commonProps} />
      
      case PricingStrategies.FLAT:
        return <FlatAccountCard {...commonProps} />
      
      case PricingStrategies.REVENUE_ATTRIBUTION:
        return <RevenueAccountCard {...commonProps} />
      
      case PricingStrategies.SERVES:
      default:
        return <ServesAccountCard {...commonProps} />
    }
  }

  return (
    <Layout.AnnotatedSection
      title={t('SettingsPage.Sections.Account.title')}
      description={t('SettingsPage.Sections.Account.description')}
    >
      <BlockStack gap="500">
        {renderAccountCard()}
      </BlockStack>
      <BillingModal open={bmOpen} setOpen={setBMOpen} />
    </Layout.AnnotatedSection>
  )
}

export default AccountSection

function Skeleton() {
  const { t } = useTranslation()
  return (
    <Layout.AnnotatedSection
      title={t('SettingsPage.Sections.Account.title')}
      description={t('SettingsPage.Sections.Account.description')}
    >
      <BlockStack gap={'500'}>
        <Card padding={'0'}>
          <Box padding={'400'}>
            <BlockStack gap={'300'}>
              <InlineGrid
                columns={'1fr auto auto'}
                gap={'300'}
                alignItems="center"
              >
                <BlockStack>
                  <SkeletonDisplayText size="small" />
                  <Text as="p">
                    {t('SettingsPage.Sections.Account.Card.Usage.description')}
                  </Text>
                </BlockStack>
              </InlineGrid>
            </BlockStack>
          </Box>
          <Divider />
          <Box padding={'400'}>
            <BlockStack gap={'300'}>
              <BlockStack gap={'200'}>
                <SkeletonDisplayText size="small" />
                <SkeletonBodyText lines={1} />
              </BlockStack>
              <ProgressBar size="small" tone="primary" progress={0} />
            </BlockStack>
          </Box>
          <Divider />
          <Box padding={'400'}>
            <BlockStack>
              <Text as="strong">
                {t('SettingsPage.Sections.Account.Card.Billing.title')}
              </Text>
              <InlineGrid columns={'1fr auto'} gap={'100'}>
                <Text>
                  {t('SettingsPage.Sections.Account.Card.Billing.description')}
                </Text>
                <Button disabled>
                  {t('SettingsPage.Sections.Account.Card.Billing.btnCta')}
                </Button>
              </InlineGrid>
            </BlockStack>
          </Box>
        </Card>
      </BlockStack>
    </Layout.AnnotatedSection>
  )
}
